import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import * as api from '../api/qr-codigo'
import type { LibroIn, CampoIn } from '../types'

// ============================================================================
// Admin — Queries
// ============================================================================

export function useLibros(params?: { search?: string; page?: number; page_size?: number }) {
  return useQuery({
    queryKey: ['qr-libros', params],
    queryFn: () => api.getLibros(params),
  })
}

export function useLibro(id: number | undefined) {
  return useQuery({
    queryKey: ['qr-libro', id],
    queryFn: () => api.getLibro(id!),
    enabled: !!id,
  })
}

export function useLibroStats(id: number | undefined) {
  return useQuery({
    queryKey: ['qr-libro-stats', id],
    queryFn: () => api.getStats(id!),
    enabled: !!id,
  })
}

export function useCodigos(libroId: number | undefined, params?: { estado?: string; search?: string }) {
  return useQuery({
    queryKey: ['qr-codigos', libroId, params],
    queryFn: () => api.getCodigos(libroId!, params),
    enabled: !!libroId,
  })
}

export function useCampos(libroId: number | undefined) {
  return useQuery({
    queryKey: ['qr-campos', libroId],
    queryFn: () => api.getCampos(libroId!),
    enabled: !!libroId,
  })
}

export function useRegistros(libroId: number | undefined, params?: { tipo?: string; search?: string }) {
  return useQuery({
    queryKey: ['qr-registros', libroId, params],
    queryFn: () => api.getRegistros(libroId!, params),
    enabled: !!libroId,
  })
}

// ============================================================================
// Admin — Mutations
// ============================================================================

export function useCreateLibro() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: LibroIn) => api.createLibro(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['qr-libros'] })
    },
  })
}

export function useUpdateLibro() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: LibroIn }) => api.updateLibro(id, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['qr-libros'] })
      queryClient.invalidateQueries({ queryKey: ['qr-libro', variables.id] })
    },
  })
}

export function useDeleteLibro() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.deleteLibro(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['qr-libros'] })
    },
  })
}

export function useToggleLibroStatus() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.toggleLibroStatus(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['qr-libros'] })
    },
  })
}

export function useGenerarCodigos() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ libroId, cantidad }: { libroId: number; cantidad: number }) =>
      api.generarCodigos(libroId, cantidad),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['qr-codigos', variables.libroId] })
      queryClient.invalidateQueries({ queryKey: ['qr-libro', variables.libroId] })
      queryClient.invalidateQueries({ queryKey: ['qr-libro-stats', variables.libroId] })
    },
  })
}

export function useToggleCodigoStatus() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ libroId, codigoId }: { libroId: number; codigoId: number }) =>
      api.toggleCodigoStatus(libroId, codigoId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['qr-codigos', variables.libroId] })
    },
  })
}

export function useDeleteCodigo() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ libroId, codigoId }: { libroId: number; codigoId: number }) =>
      api.deleteCodigo(libroId, codigoId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['qr-codigos', variables.libroId] })
      queryClient.invalidateQueries({ queryKey: ['qr-libro', variables.libroId] })
      queryClient.invalidateQueries({ queryKey: ['qr-libro-stats', variables.libroId] })
    },
  })
}

export function useCreateCampo() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ libroId, payload }: { libroId: number; payload: CampoIn }) =>
      api.createCampo(libroId, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['qr-campos', variables.libroId] })
    },
  })
}

export function useUpdateCampo() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ libroId, campoId, payload }: { libroId: number; campoId: number; payload: CampoIn }) =>
      api.updateCampo(libroId, campoId, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['qr-campos', variables.libroId] })
    },
  })
}

export function useDeleteCampo() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ libroId, campoId }: { libroId: number; campoId: number }) =>
      api.deleteCampo(libroId, campoId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['qr-campos', variables.libroId] })
    },
  })
}

// ============================================================================
// Público — Mutations
// ============================================================================

export function useLibroPublicInfo(uuid: string | undefined) {
  return useQuery({
    queryKey: ['qr-libro-public', uuid],
    queryFn: () => api.getLibroPublicInfo(uuid!),
    enabled: !!uuid,
  })
}

export function useValidarCodigo() {
  return useMutation({
    mutationFn: ({ uuid, codigo }: { uuid: string; codigo: string }) =>
      api.validarCodigo(uuid, codigo),
  })
}

export function useFormularioConfig(uuid: string | undefined) {
  return useQuery({
    queryKey: ['qr-formulario-config', uuid],
    queryFn: () => api.getFormularioConfig(uuid!),
    enabled: !!uuid,
  })
}

export function useCanjearCodigo() {
  return useMutation({
    mutationFn: ({ uuid, token, datos }: { uuid: string; token: string; datos: Record<string, string> }) =>
      api.canjearCodigo(uuid, token, datos),
  })
}

export function useDescargaInfo(downloadToken: string | undefined) {
  return useQuery({
    queryKey: ['qr-descarga-info', downloadToken],
    queryFn: () => api.getDescargaInfo(downloadToken!),
    enabled: !!downloadToken,
  })
}

export function useSolicitarCodigo() {
  return useMutation({
    mutationFn: ({ uuid, datos, codigoUsado }: { uuid: string; datos: Record<string, string>; codigoUsado?: string }) =>
      api.solicitarCodigo(uuid, datos, codigoUsado),
  })
}
